import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import StudentDashboardLayout from "./StudentDashboardLayout";
import {
  getAllStudentFees,
  getStudentApplicationFee,
} from "../../utils/Api";
import {
  payStudentFees,
  payApplicationFeeStudent,
} from "../../utils/RazorpayCheckout";
import RazorpayHostedCheckout from "../../RazorpayHostedCheckout";
import FeeTimeline from "../../components/fees/FeeTimeline";

const MyFees = () => {
  const [tuitionFees, setTuitionFees] = useState([]);
  const [applicationFeeData, setApplicationFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  // Single hosted-checkout payload; spread straight into RazorpayHostedCheckout.
  const [hostedCheckoutData, setHostedCheckoutData] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [allFeesRes, appFeeRes] = await Promise.allSettled([
      getAllStudentFees(),
      getStudentApplicationFee(),
    ]);

    if (allFeesRes.status === "fulfilled") {
      setTuitionFees(allFeesRes.value?.data || []);
    } else {
      console.error("Error fetching student fees:", allFeesRes.reason);
      setTuitionFees([]);
      if (allFeesRes.reason?.response?.status !== 404) {
        toast.error("Failed to fetch fee data");
      }
    }

    if (appFeeRes.status === "fulfilled") {
      setApplicationFeeData(appFeeRes.value?.data || null);
    } else {
      // 404 => no application fee applies; not an error worth surfacing.
      console.error("Error fetching application fee:", appFeeRes.reason);
      setApplicationFeeData(null);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handlePayTuition = async (
    feeStructureId,
    academicYear,
    type,
    installmentNumber = null,
  ) => {
    setPaymentLoading(true);
    try {
      const result = await payStudentFees(
        feeStructureId,
        academicYear,
        setHostedCheckoutData,
        type,
        installmentNumber,
      );
      // ₹0 waived tuition: backend marks it paid, no Razorpay redirect.
      if (result?.freeAdmission) {
        toast.success("This fee is fully waived — no payment required.");
        await fetchAll();
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(error.response?.data?.message || "Failed to create payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayApplication = async (type, installmentNumber = null) => {
    if (!applicationFeeData?.applicationId) return;
    setPaymentLoading(true);
    try {
      await payApplicationFeeStudent(
        applicationFeeData.applicationId,
        setHostedCheckoutData,
        type,
        installmentNumber,
        async () => {
          // Free admission callback (₹0 waived category).
          toast.success("Admission fee fully waived — no payment required.");
          await fetchAll();
        },
      );
    } catch (error) {
      console.error("Error creating application fee payment:", error);
      toast.error(error.response?.data?.message || "Failed to create payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <StudentDashboardLayout>
      <div className="max-w-4xl mx-auto py-6 px-2 md:px-4">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            My Fees
          </h1>
          <p className="text-gray-600 mt-1">
            Your admission and tuition fees across all years, with payment
            options and receipts.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : (
          <FeeTimeline
            applicationFeeData={applicationFeeData}
            tuitionFees={tuitionFees}
            paymentLoading={paymentLoading}
            onPayTuition={handlePayTuition}
            onPayApplication={handlePayApplication}
          />
        )}
      </div>

      {/* Single hosted checkout instance; auto-submits to Razorpay when set. */}
      {hostedCheckoutData && <RazorpayHostedCheckout {...hostedCheckoutData} />}

      <ToastContainer />
    </StudentDashboardLayout>
  );
};

export default MyFees;
